import { TypeKind, UnionType } from './types.js';
import { OclExpressionConstraint } from './constraint.js';
import { ModelAvailability } from './internal/ModelAvailability.js';
import { pathToRoot, PropertyPathWalker } from './internal/PropertyPathWalker.js';
import { PropertyValueIterable } from './internal/PropertyValueIterable.js';
import { DeepModelWalker } from './internal/ModelWalker.js';
import { ModelSystemProperties } from './internal/ModelSystemProperties.js';
import deepEqual from 'deep-equal';
import { Option, OptionGroup } from './option.js';
import { SinglePropertyMutator } from './internal/SinglePropertyMutator.js';
import { OclEngine, OclParser } from '@canvas-logic/ocl';
import { deepCopy } from './deep-copy.js';
import { ModelChecker } from './internal/ModelChecker.js';
import { ValidationResult } from './engine.js';
import { ComponentSearchVisitor, PrototypeTraverser, SimpleVisitor } from './internal/PrototypeVisitor.js';
import { engineUtils, FlatStructuralTransformer, getTypeRef } from './index.js';
import { EngineNotificationsService } from './services/notifications/EngineNotificationsService.js';
import { IdentityStructuralTransformer } from './services/transformers/IdentityStructuralTransformer.js';
import { CompatibilityService } from './compatibility/CompatibilityService.js';
import { isRestrictedTo } from './compatibility/CompatibleValues.js';
import { TypeService } from './services/TypeService.js';
import { CompatibilityFilter } from './compatibility/CompatibilityFilter.js';
import { CSPSolver } from './compatibility/CSPSolver.js';
import { CSPUtils } from './compatibility/CSPUtils.js';
import { extendedSinglePropertyLookupTypeGuard, multiPropertiesLookupRecordTypeGuard, singlePropertyLookupTypeGuard } from './lookup/ResolutionSchemaDefinition.js';
import { keepFirstAggregateFunction, ObjectHashSetWithPayload } from './lookup/ObjectHashSetWithPayload.js';
export const typeSymbol = Symbol.for('__type__');
export const optionIdSymbol = Symbol.for('__optionId__');
export const sourcesSymbol = Symbol.for('sources');
export const unAvailabilitySymbol = Symbol.for('unavailable');
export class EngineInternalAPI {
    static relinkToModel(schema, model, path) {
        let propertyValue = new PropertyPathWalker(path, schema, model, getTypeRef(model)).getPropertyValue();
        let resultingModel = EngineInternalAPI.prepareModel(schema, propertyValue.property.type, propertyValue.value);
        propertyValue.parent.value[propertyValue.property.name] = resultingModel;
        return resultingModel;
    }
    static prepareModel(schema, typeRef, prototype) {
        const functor = new PrototypeTraverser(schema, typeRef, prototype);
        const visitor = new SimpleVisitor(schema);
        const model = functor.map(visitor);
        return model;
    }
    ;
    static flatOptions(source) {
        function flatMap(o) {
            if (o instanceof Option) {
                return [o];
            }
            else {
                return o.children.reduce((acc, x) => acc.concat(flatMap(x)), []);
            }
        }
        return source.reduce((acc, x) => acc.concat(flatMap(x)), []);
    }
    static mapOptions(source, f) {
        function process(o) {
            if (o instanceof Option) {
                return f(o);
            }
            else {
                let newOG = deepCopy(o, ['children']);
                newOG.children = o.children.map(process).filter(child => child !== null);
                return newOG;
            }
        }
        return source.map(process);
    }
}
export class EngineImpl {
    constructor() {
        this.constraints = [];
        this.availabiltyService = new ModelAvailability();
        this.context = new EngineContext();
        this.notifications = new EngineNotificationsService();
        this.optimizationFlags = {
            useOneCopyOfModel: true,
            lookupValuesEarlyExist: false,
            skipModelCheck: true,
            skipModelCopyOnMutate: true,
            skipOptionCopyOnMutate: true,
            optimizePluginInjection: true,
        };
    }
    addOclConstraint(oclExpression, errorMessage) {
        this.constraints.push(new OclExpressionConstraint(oclExpression, errorMessage, this.schema));
    }
    addJsConstraint(model, constraint) {
        this.constraints.push(constraint);
        let validationResult = this.validate(model);
        if (validationResult.isInvalid) {
            this.constraints.pop();
        }
        return validationResult;
    }
    propertyValuesByPath(model, propertyPath, transformer = new IdentityStructuralTransformer(), filterNames = []) {
        var _a;
        let type = (_a = getTypeRef(model)) !== null && _a !== void 0 ? _a : this.typeRef;
        let propertyValue = new PropertyPathWalker(propertyPath, this.schema, model, type).getPropertyValue();
        if (!propertyValue) {
            return undefined;
        }
        let values = new PropertyValueIterable(this.schema, model, propertyPath).values;
        if (values && isSourcedValues(values)) {
            let resultValues = values;
            if (filterNames.length) {
                resultValues = this.filterOptionsWithFilters(model, propertyValue, propertyPath, values, filterNames);
            }
            return this.optionValues(transformer, resultValues);
        }
        else {
            if (filterNames.length > 0) {
                throw new Error('Filtering of enum values are not supported');
            }
            return values;
        }
    }
    filterOptionsWithFilters(model, propertyValue, propertyPath, options, filterNames) {
        var _a, _b;
        const filters = (_b = (_a = this.filterRegistry) === null || _a === void 0 ? void 0 : _a.getFiltersByName(filterNames)) !== null && _b !== void 0 ? _b : [];
        const propertyType = this.schema.getType(propertyValue.property.type);
        const propertyTypeRef = propertyType.getTypeReference();
        const result = this.filterOptionGroup(options, propertyTypeRef, (propValue) => {
            for (let filter of filters) {
                if (filter.definition.type !== propValue.type.typeRef) {
                    continue;
                }
                let isValid = filter.expression.evaluate(propValue.model, model);
                if (!isValid) {
                    return false;
                }
            }
            return true;
        });
        return result;
    }
    optionValuesByPath(model, propertyPath, transformer = new IdentityStructuralTransformer()) {
        let type = model[typeSymbol] ? model[typeSymbol] : this.typeRef;
        let propertyValue = new PropertyPathWalker(propertyPath, this.schema, model, type).getPropertyValue();
        if (!propertyValue) {
            return undefined;
        }
        let values = new PropertyValueIterable(this.schema, model, propertyPath).values;
        if (isSourcedValues(values)) {
            return this.optionValues(transformer, values);
        }
        return undefined;
    }
    optionValues(transformer, values) {
        const optionsResult = EngineInternalAPI.mapOptions(transformer.transform(values), (o) => {
            let newOption = deepCopy(o, ['model']);
            newOption.model = EngineInternalAPI.prepareModel(this.schema, o.type.typeRef, o.model);
            return newOption;
        });
        return optionsResult;
    }
    setSourcedOptionToCompatible(model) {
        let modelWalker = new DeepModelWalker(this.schema, model, this.typeRef);
        let propertyValue;
        while (propertyValue = modelWalker.next()) {
            if (ModelSystemProperties.isAvailable(propertyValue) &&
                propertyValue.property.opts.needSource &&
                ModelSystemProperties.getSource(propertyValue) &&
                !Array.isArray(ModelSystemProperties.getSource(propertyValue)) &&
                ModelSystemProperties.getSource(propertyValue).getOptionAvailability()) {
                let currentValueNotPassAvailabilityCheck = false;
                let currentOption;
                for (let source of ModelSystemProperties.getSource(propertyValue).getSource()) {
                    if (deepEqual(source.model, propertyValue.value)) {
                        currentOption = source;
                    }
                }
                for (let constraint of ModelSystemProperties.getSource(propertyValue).getOptionAvailability()) {
                    if (!constraint.evaluate(model, currentOption)) {
                        currentValueNotPassAvailabilityCheck = true;
                        break;
                    }
                }
                if (currentValueNotPassAvailabilityCheck) {
                    const path = pathToRoot(propertyValue);
                    let propertyValueIterable = new PropertyValueIterable(this.schema, model, path);
                    let propVal;
                    let foundCompatible = false;
                    while (!foundCompatible && (propVal = propertyValueIterable.next())) {
                        let skipOption = false;
                        for (let constraint of ModelSystemProperties.getSource(propertyValue).getOptionAvailability()) {
                            if (!constraint.evaluate(model, propVal)) {
                                skipOption = true;
                                break;
                            }
                        }
                        if (!skipOption && this.powerfulMutate(model, new SinglePropertyMutator(propertyValue.property.name, propVal))[1].isValid) {
                            propertyValue.parent.value[propertyValue.property.name] = propVal.model;
                            foundCompatible = true;
                        }
                    }
                    if (!foundCompatible) {
                        ModelSystemProperties.addToUnAvailability(propertyValue);
                        propertyValue.parent.value[propertyValue.property.name] = undefined;
                    }
                }
            }
        }
    }
    compatibleOptionsByMutator(model, factory, options) {
        const result = [];
        for (let option of options) {
            const mutator = factory.create(option);
            const [updatedModel, validationResult] = this.mutate(model, mutator);
            if (validationResult.isValid) {
                result.push(option);
            }
        }
        return result;
    }
    filterOptionGroup(propertyValues, typeRef, filter) {
        return EngineInternalAPI.mapOptions(propertyValues, (v) => {
            const basicType = this.schema.getType(typeRef);
            if (basicType instanceof UnionType) {
                if (!basicType.types.includes(v.type.typeRef)) {
                    return v;
                }
            }
            else {
                if (v.type.typeRef !== typeRef) {
                    return v;
                }
            }
            return filter(v) ? v : null;
        }).filter(o => o !== null);
    }
    compatiblePropertyValuesByPath(model, propertyPath, transformer = new IdentityStructuralTransformer()) {
        const modelTypeRef = getTypeRef(model);
        if (modelTypeRef !== this.typeRef) {
            throw new Error(`Invalid type ref: ${modelTypeRef}. Expected type: ${this.typeRef}`);
        }
        let propertyValue = new PropertyPathWalker(propertyPath, this.schema, model, this.typeRef).getPropertyValue();
        if (!propertyValue) {
            return undefined;
        }
        const values = this.getPropertyPotentiallyCompatibleValues(model, propertyPath);
        let propertyType = this.schema.getType(propertyValue.property.type);
        if (propertyType.kind === TypeKind.Enum) {
            return transformer.transform(values.filter(propValue => {
                const [, validationResult] = this.powerfulMutate(model, new SinglePropertyMutator(propertyPath, propValue));
                return validationResult.isValid;
            }));
        }
        else if (propertyType.kind === TypeKind.Primitive) {
            return values;
        }
        return this.compatibleOptionValues(values, propertyType, propertyValue, model, propertyPath, transformer);
    }
    compatibleOptionValuesByPath(model, propertyPath, transformer = new IdentityStructuralTransformer()) {
        var _a;
        const typeRef = (_a = getTypeRef(model)) !== null && _a !== void 0 ? _a : this.typeRef;
        let propertyValue = new PropertyPathWalker(propertyPath, this.schema, model, typeRef).getPropertyValue();
        if (!propertyValue) {
            return undefined;
        }
        const values = this.getPropertyPotentiallyCompatibleValues(model, propertyPath);
        let propertyType = this.schema.getType(propertyValue.property.type);
        if (propertyType.kind === TypeKind.Enum) {
            return undefined;
        }
        return this.compatibleOptionValues(values, propertyType, propertyValue, model, propertyPath, transformer);
    }
    compatibleOptionValues(values, propertyType, propertyValue, model, propertyPath, transformer) {
        const result = this.filterOptionGroup(values, propertyType.getTypeReference(), (propValue) => {
            let skipOption = false;
            const source = ModelSystemProperties.getSource(propertyValue);
            if (source && source.getOptionAvailability()) {
                for (let constraint of source.getOptionAvailability()) {
                    if (!constraint.evaluate(model, propValue)) {
                        skipOption = true;
                        break;
                    }
                }
            }
            return !skipOption && this.powerfulMutate(model, new SinglePropertyMutator(propertyPath, propValue))[1].isValid;
        });
        return transformer.transform(result);
    }
    optionForPropertyByPath(model, propertyPath) {
        return undefined;
    }
    compatibleOptionsByOption(option, propertyPath) {
        return [];
    }
    deleteConstraint(model, constraint) {
    }
    initByProduct(schema, product) {
        return this.init(schema, product.type.typeRef, product.model, product.constraints, new Map(), product.filters);
    }
    initByProductWithContext(schema, productWithContext) {
        const product = productWithContext.product;
        return this.init(schema, product.type.typeRef, product.model, product.constraints, productWithContext.compatibilityTables);
    }
    init(schema, typeRef, prototype, constraints = [], compatibilityTables = new Map(), filters = null) {
        this.schema = schema;
        this.filterRegistry = filters;
        this.typeRef = typeRef;
        this.constraints = constraints;
        this.compatibilityService = new CompatibilityService(this.schema);
        for (let [tableName, table] of compatibilityTables.entries()) {
            this.compatibilityService.addCompatibilityTable(table, tableName);
        }
        OclEngine.Utils.typeDeterminerFn = (obj) => {
            return obj[typeSymbol];
        };
        let model = EngineInternalAPI.prepareModel(schema, typeRef, prototype);
        let [newModel, availChanges] = this.availabiltyService.applyModel(this.schema, model, this.typeRef);
        let error = this.validate(newModel);
        if (error.isInvalid) {
            throw new Error(error.errorMessage);
        }
        return model;
    }
    mutate(model, mutator) {
        return this.powerfulMutate(model, mutator, 0);
    }
    mutateWithOption(model, option, mutator) {
        return [model, ValidationResult.Valid()];
    }
    simpleMutate(model, ...mutators) {
        let availChanges = [];
        let newModel = model;
        if (!EngineImpl.skipModelCopyOnMutate) {
            newModel = deepCopy(model);
        }
        for (let mutator of mutators) {
            let error;
            [newModel, error] = mutator.mutate(this, this.typeRef, newModel);
            if (error.isInvalid) {
                return [model, error, availChanges];
            }
        }
        [newModel, availChanges] = this.availabiltyService.applyModel(this.schema, newModel, this.typeRef);
        let error = this.validate(newModel);
        return [newModel, error, availChanges];
    }
    powerfulMutate(model, mutator, cycleNum = 0) {
        if (cycleNum > 10) {
            return [model, ValidationResult.Error('Stabilization mutation error(recursion limit exceeded)!')];
        }
        let [newModel, error, availChanges] = this.simpleMutate(model, mutator);
        if (error.isInvalid && availChanges.length) {
            let variants = [];
            for (let achange of availChanges) {
                variants.push(this.propertyValuesByPath(newModel, achange.property.name));
            }
            let combinations = combi(...variants);
            for (let com of combinations) {
                let mutators = [];
                let idx = 0;
                for (let achange of availChanges) {
                    mutators.push(new SinglePropertyMutator(achange.property.name, com[idx++]));
                }
                let [pnew, perr] = this.simpleMutate(newModel, ...mutators);
                if (perr.isValid) {
                    return [pnew, perr];
                }
            }
        }
        return [newModel, error];
    }
    propertiesState(model) {
        return { available: [], unavailable: [] };
    }
    isPropertyAvailable(propertyPath, model) {
        let propertyValue = new PropertyPathWalker(propertyPath, this.schema, model, this.typeRef).getPropertyValue();
        if (propertyValue) {
            return ModelSystemProperties.isAvailable(propertyValue);
        }
        return false;
    }
    validate(model) {
        let checker = new ModelChecker();
        let validationResult = checker.checkModel(this.schema, model, this.typeRef);
        if (validationResult.isInvalid) {
            return validationResult;
        }
        this.schema.extendWithPrototype(model, model[typeSymbol]);
        const compatibilityTablesResult = this.validateCompatibilityTables(model);
        if (compatibilityTablesResult.isInvalid) {
            return compatibilityTablesResult;
        }
        for (const constraint of this.constraints) {
            if (!constraint.evaluate(model)) {
                return ValidationResult.Error(constraint.errorMessage);
            }
        }
        return ValidationResult.Valid();
    }
    setEnv(env) {
        OclParser.setEnv(env);
    }
    entitiesByComponents(model, components) {
        let type = getTypeRef(model);
        if (!type) {
            throw new Error(`Wrong model. Cannot find typeRef`);
        }
        const traverser = new PrototypeTraverser(this.schema, type, model);
        const visitor = new ComponentSearchVisitor(this.schema, components);
        traverser.map(visitor);
        return visitor.searchResult;
    }
    ;
    getPropertyPotentiallyCompatibleValues(model, propertyPath) {
        let values;
        const compatibleValues = this.compatibilityService.getCompatibleWithModelValues(model, propertyPath);
        if (isRestrictedTo(compatibleValues)) {
            values = compatibleValues.values;
        }
        else {
            values = this.propertyValuesByPath(model, propertyPath);
        }
        return values;
    }
    validateCompatibilityTables(model) {
        return this.compatibilityService.validate(model);
    }
    getLookupValues(model, lookupId) {
        const compatibilityInvolvedPaths = this.compatibilityService.getCompatibilityInvolvedOptionPaths(model, this.typeRef);
        const resolutionSchema = this.getResolutionSchema(this.typeRef);
        const resolutionInvolvedPaths = resolutionSchema.allOptions();
        const lookup = resolutionSchema.getLookupById(lookupId);
        if (!lookup) {
            throw new Error(`Cannot find lookup ${lookupId}`);
        }
        const { option: optionPath } = lookup;
        let properties;
        if (singlePropertyLookupTypeGuard(lookup)) {
            properties = [lookup.property];
        }
        else if (extendedSinglePropertyLookupTypeGuard(lookup)) {
            properties = [lookup.properties[lookup.key]];
        }
        else if (multiPropertiesLookupRecordTypeGuard(lookup)) {
            properties = Object.values(lookup.properties);
        }
        let optionsPaths = this.mergeCompatibleWithResolutionOptions(compatibilityInvolvedPaths, resolutionInvolvedPaths, optionPath);
        const optionsPotentialValues = new Map();
        for (let optionPath of optionsPaths) {
            optionsPotentialValues.set(optionPath, this.optionValuesByPath(model, optionPath, new FlatStructuralTransformer()));
        }
        const topFilters = resolutionSchema.topFiltersFor(lookupId);
        CSPUtils.applyFiltersTo(this.schema, topFilters, optionsPotentialValues, model);
        let currentOptionValues = optionsPotentialValues.get(optionPath);
        optionsPotentialValues.delete(optionPath);
        if (!currentOptionValues) {
            throw new Error(`No options available for ${lookupId}`);
        }
        let aggregateFunction = keepFirstAggregateFunction;
        let hasAggregateFunction = false;
        if (extendedSinglePropertyLookupTypeGuard(lookup) && lookup.aggregateExpression) {
            aggregateFunction = new Function('$prev, $next', lookup.aggregateExpression);
            hasAggregateFunction = true;
        }
        if (optionsPotentialValues.size == 0) {
            const set = EngineImpl.distinctPropertyValues(currentOptionValues, properties, aggregateFunction);
            return EngineImpl.toResult(set, lookup);
        }
        const validOptionPropertyValues = new ObjectHashSetWithPayload(aggregateFunction);
        const compatibilityFilter = new CompatibilityFilter(model, this.compatibilityService);
        const solver = new CSPSolver(compatibilityFilter, currentOptionValues, Array.from(optionsPotentialValues));
        solver.solve(optionPath, (currentOptionValue, _, q) => {
            const propertyValue = engineUtils.getPropertyPathValues(currentOptionValue.model, properties);
            validOptionPropertyValues.add(propertyValue, currentOptionValue);
            if (!hasAggregateFunction) {
                const filteredOptionValues = [];
                const firstGroup = q[0];
                const len = firstGroup.length;
                let i = 0;
                for (; i < len; i++) {
                    const value = firstGroup[i];
                    if (!engineUtils.equals(engineUtils.getPropertyPathValues(value.model, properties), propertyValue)) {
                        filteredOptionValues.push(value);
                    }
                }
                q[0] = filteredOptionValues;
            }
            return 'ResetFiltersAndContinueFromStart';
        });
        return EngineImpl.toResult(validOptionPropertyValues, lookup);
    }
    static toResult(set, lookup) {
        const result = [];
        const values = set.toArray();
        if (singlePropertyLookupTypeGuard(lookup)) {
            for (const value of values) {
                result.push(value[0][0]);
            }
        }
        else if (extendedSinglePropertyLookupTypeGuard(lookup)) {
            for (let i = 0; i < values.length; i++) {
                const option = values[i][1];
                const value = engineUtils.getPropertyPathValues(option.model, Object.values(lookup.properties));
                result.push(engineUtils.buildObjectByKeys(value, Object.keys(lookup.properties)));
            }
        }
        else if (multiPropertiesLookupRecordTypeGuard(lookup)) {
            const keys = Object.keys(lookup.properties);
            for (let i = 0; i < values.length; i++) {
                let value = values[i][0];
                const obj = engineUtils.buildObjectByKeys(value, keys);
                result.push(obj);
            }
        }
        return result;
    }
    static distinctPropertyValues(values, properties, aggregateFunction) {
        const validOptionPropertyValues = new ObjectHashSetWithPayload(aggregateFunction);
        values.forEach(option => {
            const optionPropertyValue = engineUtils.getPropertyPathValues(option.model, properties);
            validOptionPropertyValues.add(optionPropertyValue, option);
        });
        return validOptionPropertyValues;
    }
    getResolutionSchema(typeRef) {
        return this.schema.resolutionSchemas.get(typeRef);
    }
    mergeCompatibleWithResolutionOptions(compatibilityInvolvedPaths, resolutionInvolvedPaths, currentOptionPath) {
        if (!compatibilityInvolvedPaths.includes(currentOptionPath)) {
            return [currentOptionPath];
        }
        return compatibilityInvolvedPaths;
    }
    getLookupValue(model, lookupId) {
        const resolutionSchema = this.getResolutionSchema(this.typeRef);
        const lookup = resolutionSchema.getLookupById(lookupId);
        if (singlePropertyLookupTypeGuard(lookup)) {
            const path = TypeService.joinPath(lookup.option, lookup.property);
            return engineUtils.getPropertyPathValue(model, path);
        }
        else if (multiPropertiesLookupRecordTypeGuard(lookup) || extendedSinglePropertyLookupTypeGuard(lookup)) {
            const paths = Object.values(lookup.properties).map(p => TypeService.joinPath(lookup.option, p));
            const value = engineUtils.getPropertyPathValues(model, paths);
            return engineUtils.buildObjectByKeys(value, Object.keys(lookup.properties));
        }
    }
    isValueSelectedInLookup(model, value, lookupId) {
        const resolutionSchema = this.getResolutionSchema(this.typeRef);
        const lookup = resolutionSchema.getLookupById(lookupId);
        if (!lookup) {
            throw new Error(`Cannot find lookup ${lookupId}`);
        }
        if (singlePropertyLookupTypeGuard(lookup)) {
            const path = TypeService.joinPath(lookup.option, lookup.property);
            return engineUtils.equals(engineUtils.getPropertyPathValue(model, path), value);
        }
        else if (extendedSinglePropertyLookupTypeGuard(lookup)) {
            const path = TypeService.joinPath(lookup.option, lookup.properties[lookup.key]);
            const selectedKeyValue = engineUtils.getPropertyPathValue(value, lookup.key);
            return engineUtils.equals(engineUtils.getPropertyPathValue(model, path), selectedKeyValue);
        }
        else if (multiPropertiesLookupRecordTypeGuard(lookup)) {
            const paths = Object.values(lookup.properties).map(p => TypeService.joinPath(lookup.option, p));
            const value = engineUtils.getPropertyPathValues(model, paths);
            return engineUtils.equals(engineUtils.buildObjectByKeys(value, Object.keys(lookup.properties)), value);
        }
        throw new Error('Unhandled lookup type');
    }
}
EngineImpl.skipModelCopyOnMutate = false;
export class EngineContext {
    enterConstraint(constraintId) {
    }
    exitConstraint(constraintId) {
    }
    touchProperty(propertyId) {
    }
    propertyChanged(propertyId) {
    }
}
function combiInternal(list, size, ...args) {
    let idx = 0;
    let currentArg = args.shift();
    size = size / currentArg.length;
    while (idx < list.length) {
        for (let arg of currentArg) {
            for (let i = 0; i < size; i++) {
                list[idx++].push(arg);
            }
        }
    }
    if (args.length == 0) {
        return list;
    }
    return combiInternal(list, size, ...args);
}
export function combi(...args) {
    let size = 1;
    for (let arg of args) {
        size = size * arg.length;
    }
    let result = Array(size);
    for (let i = 0; i < size; i++) {
        result[i] = [];
    }
    return combiInternal(result, size, ...args);
}
function isSourcedValues(xs) {
    if (!xs.length) {
        return false;
    }
    const firstElement = xs[0];
    return (firstElement instanceof Option) || (firstElement instanceof OptionGroup);
}
//# sourceMappingURL=engine-impl.js.map